/* eslint-disable react/no-unescaped-entities, no-console */

import React, { useState, useEffect } from 'react';
import { navigate } from 'gatsby';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import '../styles/index.css';
import AllHealthIcon from '../svgs/all-health-black.svg';
import { isBrowser } from '../services/msal';
import Loader from '../components/loader';

const Layout = styled(Box)`
  box-sizing: border-box;
  display: flex;
  flex-flow: wrap;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  position: absolute;
`;

const Container = styled(Box)`
  box-sizing: border-box;
  width: 432px;
  margin: 0;
  padding: 72px 48px 72px 48px;
  position: relative;
  background: #f7f7f7;
  border-radius: 12px;
  padding-bottom: 72px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`;

const HeaderTitle = styled.div`
  width: 100%;
  margin: 51px 0 36px 0;
  text-align: center;
  font-family: Gotham SSm;
  font-size: 19px;
  color: rgb(80, 86, 97);
  font-weight: 700;
  line-height: 1.26316;
`;

const InfoText = styled.span`
  display: block;
  margin-bottom: 36px;
  font-family: Gotham SSm;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  color: rgba(80, 86, 97, 0.8);
  text-align: center;
`;

const SubInfoText = styled.span`
  display: block;
  margin-top: 36px;
  padding-top: 16px;
  font-family: Gotham SSm;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  color: rgba(80, 86, 97, 0.5);
  border-top: 1px solid #dedede;
`;

const InputLabel = styled.span`
  margin: 0px;
  font-family: Gotham SSm;
  color: rgb(80, 86, 97);
  font-size: 11px;
  font-weight: 700;
  line-height: 1.63636;
`;

const Input = styled(OutlinedInput)`
  && {
    background-color: white;
    margin-bottom: 18px;
    height: 50px;
    font-size: 12px;
    font-family: 'Gotham SSm', 'Helvetica Neue', Arial, Helvetica, sans-serif;
    font-weight: 500;
    color: rgb(80, 86, 97);
    box-sizing: border-box;
    cursor: text;
    display: inline-flex;
    align-items: center;
    position: relative;
    border-radius: 4px;
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: rgb(21, 188, 207) !important;
    border-width: 2px;
  }

  &.Mui-disabled {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const ErrorAlert = styled(Alert)`
  && {
    margin-bottom: 16px;
    font-family: Gotham SSm;
    font-size: 12px;
    font-weight: 500;
    color: rgb(80, 86, 97);
  }
`;

const SendButton = styled(Button)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    outline: 0px;
    border: 0px;
    margin: 0px;
    cursor: pointer;
    user-select: none;
    vertical-align: middle;
    appearance: none;
    text-decoration: none;
    font-family: 'Gotham SSm', 'Helvetica Neue', Arial, Helvetica, sans-serif;
    font-weight: 700;
    font-size: 12px;
    line-height: 1.5;
    text-transform: none;
    box-shadow: rgba(80, 86, 97, 0.2) 0px 15px 18px -11px;
    border-radius: 3px;
    padding: 9px 18px;
    transition: none 0s ease 0s;
    position: relative;
    z-index: 0;
    color: rgb(255, 255, 255);
    background: rgb(80, 86, 97);
    width: 100%;
    height: 48px;
    margin-top: 36px;

    &:hover {
      background: rgb(80, 86, 97);
    }
  }

  &.Mui-disabled {
    background: rgba(0, 0, 0, 0.12);
    color: rgba(0, 0, 0, 0.26);
    box-shadow: none;
  }
`;

export default () => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Handling login redirect and navigation inside useEffect
    if (isBrowser() && !isAuthenticated) {
      const selectedRealm = localStorage.getItem('realm');
      const loginHint = localStorage.getItem('loginHint');
      if (
        selectedRealm
        && inProgress !== InteractionStatus.Startup
        && inProgress !== InteractionStatus.HandleRedirect
        && inProgress !== InteractionStatus.Login
      ) {
        instance.loginRedirect({ loginHint, scopes: ['openid'] });
      }
    }

    if (isBrowser() && isAuthenticated) {
      navigate('/');
    }
  }, [instance, isAuthenticated, inProgress]);

  const handleEmailChange = (event) => {
    setError('');
    const newValue = event?.currentTarget?.value || '';
    setEmail(newValue);
  };

  const handlePhoneChange = (event) => {
    setError('');
    const newValue = event?.currentTarget?.value || '';
    setPhone(newValue);
  };

  // to test locally run netlify functions:serve and change url to http://localhost:9999/
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (email || phone) {
      try {
        setIsLoading(true);
        const response = await fetch(
          '/.netlify/functions/find-user',
          {
            method: 'POST',
            body: JSON.stringify({
              ...(email && {
                email,
              }),
              ...(phone && {
                phoneNumber: phone,
              }),
            }),
          }
        );
        setIsLoading(false);
        if (response.status === 404) {
          setError('User not found. Please try another one');
        } else if (!response.ok) {
          setError('Something went wrong! Please try again');
        } else {
          const data = await response.json();
          const roles = data?.roles;
          const patientRole = roles?.find((role) => role.includes('patient'));
          const isPatient = !!patientRole;
          const tenantId = patientRole
            ? patientRole.split('/').pop()
            : roles[0].split('/').pop();
          localStorage.setItem('userId', data?.user?.id || '');
          localStorage.setItem('realm', isPatient ? 'patient' : 'care');
          localStorage.setItem(
            'flow',
            isPatient ? 'patient_flow' : 'clinician_flow'
          );
          localStorage.setItem('tenantId', tenantId || '');
          localStorage.setItem('loginHint', data?.user?.mail || '');
          window.location.reload();
        }
      } catch (e) {
        setIsLoading(false);
        setError('Something went wrong! Please try again');
        console.error('Error fetching data:', e);
      }
    }
  };

  if (
    isBrowser()
    && !isAuthenticated
    && (inProgress === InteractionStatus.Startup
    || inProgress === InteractionStatus.HandleRedirect
    || inProgress === InteractionStatus.Login)
  ) {
    return <Loader />;
  }

  return !isAuthenticated && isBrowser() && !localStorage.getItem('realm') ? (
    <>
      <Helmet>
        <title>all.health sign</title>
        <meta
          name="description"
          content="We detect lifestyle diseases early and help members manage existing conditons to influence health outcomes. Sign up to learn more."
        />
      </Helmet>
      <Layout>
        <Container>
          <Header>
            <AllHealthIcon width={60} height={43.01} />
            <HeaderTitle>all.health sign in</HeaderTitle>
          </Header>
          <Box>
            {error && <ErrorAlert severity="error">{error}</ErrorAlert>}
            <InfoText>
              Please enter your email address
              {' '}
              <b>OR</b>
              {' '}
              phone number to sign in
            </InfoText>
            <form id="realm-form" onSubmit={handleSubmit}>
              <FormControl fullWidth>
                <InputLabel>Email Address</InputLabel>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  notched={false}
                  onChange={handleEmailChange}
                  autoComplete="email"
                  inputProps={{ autoComplete: 'email' }}
                  disabled={!!phone}
                  style={{ marginBottom: '8px' }}
                />
              </FormControl>
              <InputLabel>OR</InputLabel>
              <FormControl fullWidth style={{ marginTop: '6px' }}>
                <InputLabel>Phone Number</InputLabel>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  notched={false}
                  value={phone}
                  onChange={handlePhoneChange}
                  autoComplete="tel"
                  inputProps={{ autoComplete: 'tel' }}
                  disabled={!!email}
                />
              </FormControl>
              <div>
                <SendButton
                  name="login"
                  type="submit"
                  disableRipple
                  disabled={(!email && !phone) || isLoading}
                >
                  {isLoading ? (
                    <CircularProgress color="inherit" size={25} />
                  ) : (
                    'Submit'
                  )}
                </SendButton>
              </div>
              <SubInfoText>
                Note: this login provides access to support information for
                active members and more info on all.health. To access your
                all.health program, download the all.health app on your phone
                and log in to your account there.
              </SubInfoText>
            </form>
          </Box>
        </Container>
      </Layout>
    </>
  ) : (
    <Helmet>
      <title>all.health sign</title>
      <meta
        name="description"
        content="We detect lifestyle diseases early and help members manage existing conditons to influence health outcomes. Sign up to learn more."
      />
      <Loader />
    </Helmet>
  );
};
